import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { Rating } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { AgGridReact } from 'ag-grid-react'
import { LoadingOverlay } from 'components/commonparts/index'
import { format } from 'date-fns'
import { useGridBase } from 'hooks/useGridBase'
import _ from 'lodash'
import { type SimpleDataListPropsBase } from 'types/dataGridTypes'
import { isEqualDate, normalizeDate } from 'utils/convertFromLocalDate'
import 'utils/mst.scss'


import type { CellEditRequestEvent, CellEditingStartedEvent, CellEditingStoppedEvent, ColDef, GetContextMenuItemsParams, IAggFuncParams, MenuItemDef, RowClassParams } from 'ag-grid-community'
import type { OrderPlan } from 'components/parts/OrderPlanTransportLot/type'

interface StockGridProps extends SimpleDataListPropsBase {
  orderPlanList: OrderPlan[]
  onOrderPlanChange?: (field: keyof OrderPlan, target: OrderPlan, oldItem: OrderPlan) => void
  onDoUpdateForRecDate?: (recDate: Date) => void
  onTransportLotForRecDate?: (recDate: Date) => void
  onOptimizeLoadFactorForRecDate?: (recDate: Date) => void
  onTransportTypeChange?: (recDate: Date, transportLotNo: number) => void
  onChangeArrivalNextDay?: (selectedData: Array<{recDate: Date, transportLotNo: number, itemCode: string}>) => void
  onChangeArrivalPrevDay?: (selectedData: Array<{recDate: Date, transportLotNo: number, itemCode: string}>) => void
  rowClassRules?: { [cssClassName: string]: (params: RowClassParams) => boolean };
  isTransportSummaryOpen?: boolean;
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
}

function expectedRankCellRenderer(params: any) {
  if (params.node.group) {
    return ''
  }

  return (
    <div className="rating-cell">
      <Rating value={params.value || 0} max={3} readOnly />
    </div>
  )
}

function DataList({
  orderPlanList,
  onOrderPlanChange,
  onDoUpdateForRecDate,
  onTransportLotForRecDate,
  onOptimizeLoadFactorForRecDate,
  onTransportTypeChange,
  onChangeArrivalNextDay,
  onChangeArrivalPrevDay,
  isLoading,
  setDownloadColumnDefs,
  rowClassRules,
  isTransportSummaryOpen = false,
  gridRef: externalGridRef,
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  isSearchVisible = false,
}: StockGridProps) {
  const { loginUserInfo } = useAuthContext()
  const transportSummaryHeight = isTransportSummaryOpen ? 160 : -30
  const searchBoxHeight = isSearchVisible ? 60 : 0
  const baseOffset = 320 // Title(40) + MasterSearchFrom(40) + SearchCondition(200) + FileDownloadData(20) + margins(20)
  const totalOffset = baseOffset + transportSummaryHeight + searchBoxHeight
  const containerStyle = useMemo(() => ({ width: '100%', height: `calc(100vh - ${totalOffset}px)` }), [totalOffset])
  const gridStyle = useMemo(() => ({ width: '100%', height: `calc(100vh - ${totalOffset - 10}px)` }), [totalOffset])

  const internalGridRef = useRef<AgGridReact<OrderPlan>>(null)
  const gridRef = externalGridRef || internalGridRef
  const isEditingRef = useRef<boolean>(false)
  const pendingEditRef = useRef<{ id: any, field: keyof OrderPlan, value: any } | null>(null)
  const hasAutoSizedColumnsRef = useRef<boolean>(false)

  // Get systemDate for comparison
  const systemDate = useMemo(() => {
    const date = normalizeDate(loginUserInfo?.systemDate ?? Date.now())
    if (date) date.setHours(0, 0, 0, 0)
    return date
  }, [loginUserInfo?.systemDate])

  // Filter orderPlanList by date range (systemDate -7 to systemDate +13)
  const filteredOrderPlanList = useMemo(() => {
    if (!orderPlanList) return orderPlanList

    return orderPlanList.map(orderPlan => ({
      ...orderPlan,
      recDate: orderPlan.recDate ? normalizeDate(orderPlan.recDate)?.toLocaleDateString('ja-JP') : ''
    }))
  }, [orderPlanList])

  // Dev Note : set column group then use rowGroup
  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      width: 250,
      field: 'recDate',
      headerName: '入荷日',
      pinned: 'left',
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              treeList: true,
              keyCreator: (params: any) => {
                if (!params || !params.value) return null;
                return String(params.value);
              },
              comparator: (valueA: any, valueB: any) => {
                const dateA = new Date(valueA)
                const dateB = new Date(valueB)
                return dateA.getTime() - dateB.getTime()
              },
              refreshValuesOnOpen: true,
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
            },
          },
        ],
        buttons: ['reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: true,
        excelMode: 'mac',
      },
      cellRendererParams: {
        suppressCount: false,
        innerRenderer: (params: any) => {
          if (params.node.level === 0) {
            const v = params.value;
            return v instanceof Date ? v.toLocaleDateString('ja-JP') : String(v ?? '');
          }

          if (params.node.level === 1) {
            const first = params.node.allLeafChildren?.[0]?.data;
            const lotNo = first?.transportLotNo ?? params.node.key;
            const typeId = first?.orderTransportTypeId ?? '';
            const typeName = first?.transportTypeName ?? '';
            const maxWeight = first?.maxWeight ?? 0;
            const maxPallet = first?.maxPalletCount ?? 0;
            if (lotNo && typeName) {
              return `${lotNo} - ([${typeId}]${typeName}(${maxWeight}t,${maxPallet}PL))`;
            }
            return "";
          }

          if (params.node.level === 2) {
            return ""
          }

          return String(params.value ?? '');
        },
      },
    }),
    []
  )

  const {
    formatDecimalValue,
    dateFormatter,
    parseDecimalValue,
    parseDateValue,
    isEnableEdit,
    onGridReady,
    localeText,
    numberValueFormatter,
    numberValueParser,
    defaultColDef: baseDefaultColDef,
    autoSizeStrategy,
    longTextEditorParams,
    longTextValueFormatter,
    longTextValueParser,
    longTextValueSetter,
    dateTreeSetFilterParams,
  } = useGridBase({
    gridRef,
    isLoading,
  })

  const defaultColDef = useMemo(() => ({
    ...baseDefaultColDef,
    mainMenuItems: undefined,
  }), [baseDefaultColDef])

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed()) return;

    api.setGridOption('loading', !!isLoading)

    if (isLoading) return

    // wait for rowData apply done then handle NoRows/hide to avoid race
    queueMicrotask(() => {
      if (api.isDestroyed()) return
      if ((filteredOrderPlanList?.length ?? 0) === 0) {
        api.showNoRowsOverlay()
      } else {
        api.hideOverlay()
        // Auto-size only once when initial data is available to avoid render loops.
        if (!hasAutoSizedColumnsRef.current) {
          setTimeout(() => {
            if (api.isDestroyed()) return
            const colIds = api.getColumns()?.map((c: any) => c.getColId()) ?? []
            if (colIds.length > 0) {
              api.autoSizeColumns(colIds)
            }
            hasAutoSizedColumnsRef.current = true
          }, 150)
        }
      }
    })
  }, [isLoading, filteredOrderPlanList]);

  // Date formatter for download (date only, no time)
  const downloadDateFormatter = useCallback(
    (params: any) => {
      if (!params.value) {
        return ''
      }
      return format(new Date(params.value), 'yyyy-MM-dd')
    },
    []
  )

  // Decimal formatter
  const handleformatDecimalValue = useCallback(
    (params: any) => {
      if (!params) {
        return ''
      }
      return formatDecimalValue(params, 1)
    },
    []
  )

  // Dev Note : Define column style
  const getCellClassRules = () => ({
    'error-cell': (params: any) => {
      const rowData = params.data as typeof params.data
      const { field } = params.colDef
      const hasError = rowData?.errors && Array.isArray(rowData.errors[field]) && rowData.errors[field].length > 0
      return hasError || false
    },
    'current-updated-cell': (params: any) => {
      if (params.node.level === 0 || params.node.level === 1) return false
      const rowData = params.data as typeof params.data
      const { field } = params.colDef
      return (
        rowData?.isEdited &&
        (!rowData.errors?.[field] || rowData.errors[field].length === 0) &&
        rowData.editedFields?.has(field)
      )
    },
    'editable-cell': (params: any) => {
      if (params.node.level === 0 || params.node.level === 1) return false
      if (!isEnableEdit(params.colDef.editable)) return false
      const { editable } = params.colDef
      let iseditable = false
      if (typeof editable === 'function') {
        iseditable = editable(params)
      } else {
        iseditable = editable
      }
      return iseditable
    },
    'future-arrival-exists': (params: any) => {
      if (params.node?.level === 0 || params.node?.level === 1) return false
      const rowData = params.data as OrderPlan
      const { field } = params.colDef
      return rowData?.orderQuantityRec !== null && rowData?.orderQuantityRec !== undefined && field === 'lotQuantity'
    },
    'order-confirmed': (params: any) => {
      if (params.node?.level === 0 || params.node?.level === 1) return false
      const rowData = params.data as OrderPlan
      const { field } = params.colDef
      return rowData?.isOrderConfirmed === true && field === 'lotQuantity'
    },
    'updated-cell': (params: any) => {
      if (params.node?.level === 0 || params.node?.level === 1) return false
      const { field } = params.colDef
      return (params.data?.isEdited === true || params.data?.isUpdatedArrival === true) && field === 'lotQuantity'
    },
  })


  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: '入荷日',
        field: 'recDate',
        rowGroup: true,
        hide: true,
        showRowGroup: 'recDate',
        pinned: 'left',
        valueFormatter: dateFormatter,
        valueParser: parseDateValue,
        sort: 'asc',
        sortIndex: 0, // First priority
        comparator: (valueA: any, valueB: any) => {
        const dateA = new Date(valueA)
        const dateB = new Date(valueB)
        return dateA.getTime() - dateB.getTime()
      }
      },
      { headerName: '発注日',
        field: 'orderDate',
        valueFormatter: dateFormatter,
        valueParser: parseDateValue,
        cellClassRules: {
          'todays-order-cell': (params: any) => {
            if (params.node.level === 0 || params.node.level === 1) return false
            const rowData = params.data as OrderPlan
            return !!(rowData?.orderDate && systemDate && isEqualDate(rowData.orderDate, systemDate))
          }
        } as any,
        filterParams: {
          filters: [
            {filter: 'agDateColumnFilter'},
            {filter: 'agSetColumnFilter',
             filterParams: dateTreeSetFilterParams,
            },
          ],
          closeOnApply: true,
        },
        // width: 115,
        // minWidth: 115,
      },
      {
        headerName: '予測ランク',
        field: 'expectedRank',
        cellClass: 'number-cell',
        cellClassRules: {
          'alert_hight': (params: any) => {
                if (params.node?.level === 0 || params.node?.level === 1) return false
                const rowData = params.data as OrderPlan
                return rowData?.isExpectedRankAlert === true
              },
        } as any,
        editable: false,
        sortable: false,
        cellRenderer: expectedRankCellRenderer,
      },
      {
        headerName: '輸送ロット',
        field: 'transportLotNo',
        rowGroup: true,
        editable: isEnableEdit(),
        cellClass: 'number-cell',
        cellEditor: 'agNumberCellEditor',
        // cellEditorParams: {
        //   step: 1e-18,
        //   min: 0,
        // },
        // width: 185,
        // minWidth: 140,
        cellClassRules: getCellClassRules(),
        onCellValueChanged: (params) => {
          params.api.refreshClientSideRowModel('group')
        },
        comparator: (valueA: any, valueB: any) => {
          // Put null/undefined values at the bottom
          if (valueA == null && valueB == null) return 0
          if (valueA == null) return 1  // A is null, put at bottom
          if (valueB == null) return -1 // B is null, put at bottom
          return valueA - valueB // Normal numeric sort
        },
        cellRenderer: (params: any) => {
          if (params.node.group) {
            const firstChild = params.node.childrenAfterFilter?.[0]
            const childData = firstChild?.data

            if (childData?.transportLotNo) {
              return `${childData.transportLotNo}`
            }

            return ' '
          }
          return params.value
        },
        valueParser: numberValueParser,
        valueFormatter: numberValueFormatter,
        sort: 'asc',
        sortIndex: 1, // Second priority
        filter: 'agNumberColumnFilter',
      },
      { headerName: '商品コード',
        field: 'itemCode',
        cellClass: 'number-cell',
        // width: 140,
        // minWidth: 140,
      },
      { headerName: '社内商品コード',
        field: 'orgItemCode',
        cellClass: 'number-cell',
        // width: 130,
        // minWidth: 130,
      },
      { headerName: '商品名称',
        field: 'itemName',
        // width: 300,
        // minWidth: 300
      },
      {
        headerName: '発注数',
        field: 'lotQuantity',
        aggFunc: 'sum',
        cellClass: 'number-cell',
        editable: isEnableEdit(),
        filter: 'agNumberColumnFilter',
        // width: 115,
        // minWidth: 115,
        cellClassRules: getCellClassRules(),
        valueParser: numberValueParser
      },
      { 
        headerName: 'パレット最大積付数',
        field: 'maxPalletQuantity',
        cellClass: 'number-cell',
        filter: 'agNumberColumnFilter',
        // width: 180,
        // minWidth: 180,
      },
      {
        headerName: 'パレット数',
        field: 'lotQuantityPallet',
        aggFunc: 'sum',
        cellClass: 'number-cell',
        valueFormatter: (params) => formatDecimalValue(params, 1),
        valueParser: (params) => parseDecimalValue(params, 1),
        filter: 'agNumberColumnFilter',
        // width: 140,
        // minWidth: 140,
      },
      {
        headerName: '重量（ｔ）',
        field: 'lotItemWeight',
        aggFunc: 'avgLoadPercent',
        cellClass: 'number-cell',
        valueFormatter: (params) => formatDecimalValue(params, 3),
        valueParser: (params) => parseDecimalValue(params, 3),
        filter: 'agNumberColumnFilter',
        // width: 140,
        // minWidth: 140,
      },
      {
        headerName: '在庫日数',
        field: 'stockDays',
        cellClass: 'number-cell',
        cellClassRules: {
          'alert_hight': (params) => {
            if (params.node?.level === 2 && params.data?.stockDays !== undefined && params.data?.stockDaysSetting !== undefined) {
              return params.data.stockDays <= params.data.stockDaysSetting
            }
            return false
          }
        },
        valueFormatter: (params) => {
          const { value } = params;
          if (value === null || value === undefined || Number.isNaN(value)) return '-';
          const val = Number(value);
          if (val <= 0) return '0';
          if (val > 999) return '999 +';
          return val.toFixed(1);
        },
        valueParser: (params) => parseDecimalValue(params, 1),
        filter: 'agNumberColumnFilter',
        // width: 120,
        // minWidth: 120,
      },
      { 
        headerName: '在庫日数影響',
        field: 'stockDaysInfluence',
        cellClass: 'number-cell',
        cellClassRules: {
          'current-updated-cell': (params: any) => {
            if (params.node?.level === 2 && params.data?.stockDaysInfluence !== undefined) {
              return (params.data.stockDaysInfluence !== 0 && !Number.isNaN(params.data.stockDaysInfluence))
            }
            return false
          }
        } as any,
        valueFormatter: (params) => {
          if (!params.value || params.value === null || params.value === undefined || params.value === '') return '-';
          const numericValue = Number(params.value);
          if (Number.isNaN(numericValue)) return params.value?.toString() || '';
          const multiplier = 10 ** 1;
          const rounded = Math.round(numericValue * multiplier) / multiplier;
          return rounded.toFixed(1);
        },
        valueParser: (params) => {
          if (!params.newValue || params.newValue === null || params.newValue === undefined || params.newValue === '') return null;
          const numericValue = Number(params.newValue);
          if (Number.isNaN(numericValue)) return null;
          const multiplier = 10 ** 1;
          return Math.round(numericValue * multiplier) / multiplier;
        },
        filter: 'agNumberColumnFilter',
        // width: 120,
        // minWidth: 120,
      },
      // {
      //   headerName: '変更前発注日',
      //   field: 'orderDatePrev',
      //   width: 150,
      //   minWidth: 150,
      // },
      {
        headerName: '備考',
        field: 'remarks',
        editable: isEnableEdit(),
        width: 200,
        minWidth: 200,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: longTextEditorParams,
        valueFormatter: longTextValueFormatter,
        valueParser: longTextValueParser,
        valueSetter: longTextValueSetter,
        cellClassRules: getCellClassRules(),
      },
    ],
    [dateFormatter, formatDecimalValue, isEnableEdit, parseDateValue, parseDecimalValue, longTextEditorParams, longTextValueFormatter, longTextValueParser, longTextValueSetter]
  )

  // Grid Functions
  const getAllDescendants = useCallback((node: any): any[] => {
    const descendants: any[] = []
    const traverse = (currentNode: any) => {
      if (currentNode.childrenAfterFilter && currentNode.childrenAfterFilter.length > 0) {
        currentNode.childrenAfterFilter.forEach((child: any) => {
          descendants.push(child)
          traverse(child)
        })
      }
    }
    traverse(node)
    return descendants
  }, [])

  const aggFuncs = useMemo(
    () => ({
      avgLoadPercent: (params: IAggFuncParams) => {
        const children = params.rowNode?.childrenAfterFilter ?? []
        const level = params.rowNode?.level ?? 0

        if (level === 0) {
          const allDescendants = getAllDescendants(params.rowNode)
          const totalLotItemWeight = allDescendants.reduce((sum: number, node: any) => {
            if (node.data) {
              const weight = node.data?.lotItemWeight || 0
              return sum + weight
            }
            return sum
          }, 0)
          return `${totalLotItemWeight.toFixed(3)}t`
        }

        const totalLotItemWeight = children.reduce((sum: number, child: any) => {
          if (child.data) {
            const weight = child.data?.lotItemWeight || 0
            return sum + weight
          }
          return sum
        }, 0)

        const maxWeight = _.maxBy(children, (child: any) => child.data?.maxWeight ?? 0)?.data?.maxWeight ?? 0
        if(maxWeight === 0) {
          return '-'
        }
        return `${totalLotItemWeight.toFixed(3)}t(${((totalLotItemWeight / maxWeight) * 100).toFixed(1)}%)` || '-'
      },
    }),
    [getAllDescendants]
  )

  // Dev Note : Define column download excel
  useEffect(() => {
    if (setDownloadColumnDefs) {
      setDownloadColumnDefs([
        {
          headerName: 'PSI企業ID',
          children: [{ headerName: 'company_id', field: 'companyId', cellClass: 'numberType' }],
        },
        {
          headerName: 'PSIセンターID',
          children: [{ headerName: 'center_id', field: 'centerId', cellClass: 'numberType' }],
        },
        {
          headerName: '自社センターID',
          children: [{ headerName: 'org_center_id', field: 'orgCenterId', cellClass: 'stringType' }],
        },
        {
          headerName: 'センター名',
          children: [{ headerName: 'center_name', field: 'centerName', cellClass: 'stringType' }],
        },
        {
          headerName: '仕入先ID',
          children: [{ headerName: 'org_supplier_id', field: 'orgSupplierId', cellClass: 'stringType' }],
        },
        {
          headerName: '仕入先名',
          children: [{ headerName: 'supplier_name', field: 'supplierName', cellClass: 'stringType' }],
        },
        {
          headerName: '発注日',
          children: [{ headerName: 'order_date', field: 'orderDate', valueFormatter: downloadDateFormatter }],
        },
        {
          headerName: '予測ランク',
          children: [{ headerName: 'expected_rank', field: 'expectedRank', cellClass: 'numberType' }],
        },
        {
          headerName: '入荷日',
          children: [{ headerName: 'rec_date', field: 'recDate', valueFormatter: downloadDateFormatter }],
        },
        {
          headerName: '輸送番号',
          children: [{ headerName: 'transport_lot_no', field: 'transportLotNo', cellClass: 'numberType' }],
        },
        {
          headerName: '輸送手段ID',
          children: [{ headerName: 'order_transport_type_id', field: 'orderTransportTypeId', cellClass: 'numberType' }],
        },
        {
          headerName: '輸送手段名',
          children: [{ headerName: 'transport_type_name', field: 'transportTypeName', cellClass: 'stringType' }],
        },
        {
          headerName: '最大積載重量',
          children: [{ headerName: 'max_weight', field: 'maxWeight', cellClass: 'decimalType' }],
        },
        {
          headerName: 'パレット最大積載枚数',
          children: [{ headerName: 'max_pallet_count', field: 'maxPalletCount', cellClass: 'decimalType' }],
        },
        {
          headerName: '商品コード',
          children: [{ headerName: 'item_code', field: 'itemCode', cellClass: 'stringType' }],
        },
        {
          headerName: '社内商品コード',
          children: [{ headerName: 'org_item_code', field: 'orgItemCode', cellClass: 'stringType' }],
        },
        {
          headerName: '商品名',
          children: [{ headerName: 'item_name', field: 'itemName', cellClass: 'stringType' }],
        },
        {
          headerName: 'パレット最大積付数',
          children: [{ headerName: 'max_pallet_quantity', field: 'maxPalletQuantity', cellClass: 'numberType' }],
        },
        {
          headerName: 'ケースサイズ 重量 (g)',
          children: [{ headerName: 'weight', field: 'weight', cellClass: 'decimalType' }],
        },
        {
          headerName: 'ロット内ケース数',
          children: [{ headerName: 'lot_quantity', field: 'lotQuantity', cellClass: 'numberType' }],
        },
        {
          headerName: 'ロット内パレット数',
          children: [{ headerName: 'lot_quantity_pallet', field: 'lotQuantityPallet', cellClass: 'decimalType', valueFormatter: handleformatDecimalValue }],
        },
        {
          headerName: 'ロット内重量',
          children: [{ headerName: 'lot_item_weight', field: 'lotItemWeight', cellClass: 'decimalType' }],
        },
        {
          headerName: '発注コメント',
          children: [{ headerName: 'remarks', field: 'remarks', cellClass: 'stringType' }],
        },
        {
          headerName: '変更前ロット内ケース数',
          children: [{ headerName: 'lot_quantity_prev', field: 'lotQuantityPrev', cellClass: 'numberType' }],
        },
      ])
    }
  }, [setDownloadColumnDefs, downloadDateFormatter, handleformatDecimalValue])

  const commitChange = useCallback((id: any, field: keyof OrderPlan, value: any) => {
    const oldItem = orderPlanList && orderPlanList.find((row) => row.id === id)
    if (!oldItem || !field) return
    if (oldItem[field] === value) return
    let processedValue = value
    if (field === 'lotQuantity' && (value === null || value === undefined)) {
      processedValue = 0
    }
    const newItem = { ...oldItem, [field]: processedValue }
    onOrderPlanChange?.(field, newItem, oldItem)
  }, [orderPlanList, onOrderPlanChange])

  const onCellEditRequest = useCallback(
    (event: CellEditRequestEvent): (() => void) | undefined => {
      const { data, newValue } = event
      const field = event.colDef.field as keyof OrderPlan
      if (!data || !field) return undefined
      // If editing, save the change temporarily, do not commit
      if (isEditingRef.current) {
        pendingEditRef.current = { id: (data as any).id, field, value: newValue }
        return undefined
      }
      // If not in editing state, commit immediately (case paste quickly)
      commitChange((data as any).id, field, newValue)
      return undefined
    },
    [commitChange]
  );

  const onCellEditingStarted = useCallback((event: CellEditingStartedEvent) => {
    isEditingRef.current = true
    pendingEditRef.current = null
  }, [])

  const onCellEditingStopped = useCallback((event: CellEditingStoppedEvent) => {
    isEditingRef.current = false
    const pending = pendingEditRef.current
    pendingEditRef.current = null
    if (!pending) return
    commitChange(pending.id, pending.field, pending.value)
  }, [commitChange])

  const getSelectedRowDataForMove = useCallback((api: any): {selectedData: Array<{recDate: Date, transportLotNo: number, itemCode: string}>, disabledLevel0: boolean, disabledLevel1: boolean, level0Count: number, level1Count: number} => {
    const cellRanges = api.getCellRanges()
    if (!cellRanges || cellRanges.length === 0) return {
      selectedData: [],
      disabledLevel0: false,
      disabledLevel1: false,
      level0Count: 0,
      level1Count: 0
    }

    const { startRow, endRow } = cellRanges[0]
    const startIdx = startRow?.rowIndex
    const endIdx = endRow?.rowIndex
    if (startIdx === undefined || endIdx === undefined) return {
      selectedData: [],
      disabledLevel0: false,
      disabledLevel1: false,
      level0Count: 0,
      level1Count: 0
    }

    const from = Math.min(startIdx, endIdx)
    const to = Math.max(startIdx, endIdx)

    const selectedDataList: Array<{recDate: Date, transportLotNo: number, itemCode: string}> = []
    let level0Count = 0
    let level1Count = 0
    let transportLotNoFlag = false
    for (let i = from; i <= to; i += 1) {
      const rowNode = api.getDisplayedRowAtIndex(i)
      if(rowNode.level === 0) {
        level0Count += 1
        if (i === from) {
          level0Count -= 1
        }
      }
      if(rowNode.level === 1) {
        level1Count += 1
        if (i !== from) {
          transportLotNoFlag = true
        }
      }
      if (rowNode.level === 2) {
        if (transportLotNoFlag) {
          level1Count += 1
        }
        selectedDataList.push({
          recDate: new Date(rowNode.data.recDate),
          transportLotNo: rowNode.data.transportLotNo || null,
          itemCode: rowNode.data.itemCode
        })
      }
    }

    return {
      selectedData: selectedDataList,
      disabledLevel0: level0Count > 0,
      disabledLevel1: level1Count > 0,
      level0Count,
      level1Count
    }
  }, [])

  const handleMoveMultipleToNextDay = useCallback((api: any) => {
    const { selectedData, disabledLevel0, disabledLevel1, level0Count, level1Count } = getSelectedRowDataForMove(api)
    if (selectedData.length === 0) {
      return
    }
    onChangeArrivalNextDay?.(selectedData)
  }, [getSelectedRowDataForMove, onChangeArrivalNextDay])

  const handleMoveMultipleToPrevDay = useCallback((api: any) => {
    const { selectedData, disabledLevel0, disabledLevel1, level0Count, level1Count } = getSelectedRowDataForMove(api)
    if (selectedData.length === 0) {
      return
    }
    onChangeArrivalPrevDay?.(selectedData)
  }, [getSelectedRowDataForMove, onChangeArrivalPrevDay])

  // create context menu
  const getContextMenuItems = useCallback((params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    const { selectedData, disabledLevel0, disabledLevel1, level0Count, level1Count } = getSelectedRowDataForMove(params.api)
    const { node } = params
    if (!node) {
      return []
    }
    
    // Get data from the node itself (for leaf nodes) or from the first child (for group nodes)
    let { data } = node
    if (node.group && (!data || !data.recDate)) {
      // For group nodes, try to get data from the first child
      const firstChild = node.allLeafChildren?.[0] || node.childrenAfterFilter?.[0]
      if (firstChild && firstChild.data) {
        data = firstChild.data
      }
    }

    const result: (string | MenuItemDef)[] = [
      {
        name: 'コピーする',
        action: () => params.api.copySelectedRangeToClipboard(),
      },
      {
        name: '行コピーする',
        action: () => params.api.copySelectedRowsToClipboard(),
      },
      'copyWithHeaders',
      'separator',
      {
        name: '貼り付ける',
        action: () => params.api.pasteFromClipboard(),
        disabled: !isEnableEdit(),
      },
      'separator',
      {
        name: 'この日まで発注を確定する',
        action: () => data?.recDate && onDoUpdateForRecDate?.(new Date(data.recDate)),
        disabled: !data?.recDate || disabledLevel0,
      },
      {
        name: 'この日の車両割り当てをやり直す',
        action: () => data?.recDate && onTransportLotForRecDate?.(new Date(data.recDate)),
        disabled: !data?.recDate || disabledLevel0,
      },
      {
        name: 'この日の積載率を調整する',
        action: () => data?.recDate && onOptimizeLoadFactorForRecDate?.(new Date(data.recDate)),
        disabled: !data?.recDate || disabledLevel0,
      },
      'separator',
      {
        name: '輸送種類変更',
        action: () => data?.recDate && data?.transportLotNo && onTransportTypeChange?.(new Date(data.recDate), data.transportLotNo),
        disabled: !data?.recDate || !data?.transportLotNo || level0Count > 1 || level1Count > 1 || disabledLevel0
      },
      {
        name: '入荷を次の日に変更',
        action: () => handleMoveMultipleToNextDay(params.api),
        disabled: node.level === 0 || node.level === 1 || disabledLevel0 || disabledLevel1,
      },
      {
        name: '入荷を前の日に変更',
        action: () => handleMoveMultipleToPrevDay(params.api),
        disabled: node.level === 0 || node.level === 1 || disabledLevel0 || disabledLevel1,
      },
    ]

    return result
  }, [onDoUpdateForRecDate, onTransportLotForRecDate, onOptimizeLoadFactorForRecDate, onTransportTypeChange, handleMoveMultipleToNextDay, handleMoveMultipleToPrevDay, isEnableEdit])

  // Generate stable row ID for ag-grid to track nodes properly
  const getRowId = useCallback((params: any) => {
    const { data } = params
    if (!data) return undefined
    return data.id || `${data.recDate}_${data.transportLotNo}_${data.itemCode}_${data.orgItemCode}`
  }, [])

  return (
    <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            rowData={filteredOrderPlanList}
            ref={gridRef}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            // autoSizeStrategy={autoSizeStrategy}
            onGridReady={onGridReady}
            autoGroupColumnDef={autoGroupColumnDef}
            suppressAggFuncInHeader
            groupDefaultExpanded={-1}
            aggFuncs={aggFuncs}
            localeText={localeText}
            loadingOverlayComponent={LoadingOverlay}
            readOnlyEdit
            onCellEditRequest={onCellEditRequest}
            onCellEditingStarted={onCellEditingStarted}
            onCellEditingStopped={onCellEditingStopped}
            stopEditingWhenCellsLoseFocus
            getContextMenuItems={getContextMenuItems as any}
            rowClassRules={rowClassRules}
            suppressScrollOnNewData
            getRowId={getRowId}
            findSearchValue={externalFindSearchValue}
            onFindChanged={externalOnFindChanged}
          />
        </div>
      </div>
    </div>
  )
}

export default DataList
